import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Search, Filter, SortAsc, RefreshCw } from 'lucide-react'
import { jobsApi, applicationsApi } from '@/lib/api'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PipelineColumn } from './PipelineColumn'
import { CandidateDrawer } from '../candidates/CandidateDrawer'
import { toast } from '@/components/ui/toaster'

interface Application {
  id: string
  stageId: string
  candidate: {
    id: string
    name: string
    email: string
    avatarUrl?: string
    rating?: number
  }
  appliedAt: string
  source?: string
  hasResume?: boolean
  interviewCount?: number
}

interface Stage {
  id: string
  name: string
  color: string
  order: number
}

interface KanbanBoardProps {
  jobId: string
}

export function KanbanBoard({ jobId }: KanbanBoardProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'rating'>('date')
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const queryClient = useQueryClient()

  // Fetch pipeline stages
  const { data: stages = [], isLoading: stagesLoading } = useQuery({
    queryKey: ['job-pipeline', jobId],
    queryFn: () => jobsApi.getPipeline(jobId),
  })

  // Fetch applications
  const { data: applications = [], isLoading: applicationsLoading, refetch } = useQuery({
    queryKey: ['job-applications', jobId],
    queryFn: () => jobsApi.getApplications(jobId),
  })

  // Move application mutation
  const moveApplication = useMutation({
    mutationFn: ({ applicationId, stageId }: { applicationId: string; stageId: string }) =>
      applicationsApi.move(applicationId, stageId),
    onMutate: async ({ applicationId, stageId }) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: ['job-applications', jobId] })

      const previousApplications = queryClient.getQueryData(['job-applications', jobId])

      queryClient.setQueryData(['job-applications', jobId], (old: Application[] = []) =>
        old.map((app) =>
          app.id === applicationId ? { ...app, stageId } : app
        )
      )

      return { previousApplications }
    },
    onError: (err, _, context) => {
      // Rollback on error
      if (context?.previousApplications) {
        queryClient.setQueryData(['job-applications', jobId], context.previousApplications)
      }
      toast({ title: 'Failed to move application', variant: 'destructive' })
    },
    onSuccess: () => {
      toast({ title: 'Application moved', variant: 'success' })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['job-applications', jobId] })
      queryClient.invalidateQueries({ queryKey: ['job-pipeline', jobId] })
    },
  })

  // Filter and sort applications
  const filteredApplications = useMemo(() => {
    let filtered = (applications as Application[]).filter((app) => {
      if (!searchQuery) return true
      const query = searchQuery.toLowerCase()
      return (
        app.candidate.name.toLowerCase().includes(query) ||
        app.candidate.email.toLowerCase().includes(query)
      )
    })

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.candidate.name.localeCompare(b.candidate.name)
        case 'rating':
          return (b.candidate.rating || 0) - (a.candidate.rating || 0)
        case 'date':
        default:
          return new Date(b.appliedAt).getTime() - new Date(a.appliedAt).getTime()
      }
    })
  }, [applications, searchQuery, sortBy])

  // Group applications by stage
  const applicationsByStage = useMemo(() => {
    const grouped: Record<string, Application[]> = {}
    ;(stages as Stage[]).forEach((stage) => {
      grouped[stage.id] = filteredApplications.filter((app) => app.stageId === stage.id)
    })
    return grouped
  }, [filteredApplications, stages])

  const handleDrop = (applicationId: string, stageId: string) => {
    const application = (applications as Application[]).find((a) => a.id === applicationId)
    if (application && application.stageId !== stageId) {
      moveApplication.mutate({ applicationId, stageId })
    }
  }

  const handleApplicationClick = (application: Application) => {
    setSelectedApplication(application)
    setIsDrawerOpen(true)
  }

  if (stagesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const sortedStages = [...(stages as Stage[])].sort((a, b) => a.order - b.order)

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search candidates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
          <SelectTrigger className="w-[140px]">
            <SortAsc className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date">Date Applied</SelectItem>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="rating">Rating</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" size="icon" onClick={() => refetch()}>
          <RefreshCw className={`h-4 w-4 ${applicationsLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {sortedStages.map((stage) => (
          <PipelineColumn
            key={stage.id}
            stage={stage}
            applications={applicationsByStage[stage.id] || []}
            onApplicationClick={handleApplicationClick}
            onDrop={handleDrop}
          />
        ))}
      </div>

      {/* Candidate Drawer */}
      {selectedApplication && (
        <CandidateDrawer
          open={isDrawerOpen}
          onOpenChange={setIsDrawerOpen}
          candidateId={selectedApplication.candidate.id}
          applicationId={selectedApplication.id}
        />
      )}
    </div>
  )
}
